/**
 * CawoodAssessment.jsx
 * --------------------
 * Root component for the Cawood Digital Maturity Assessment.
 *
 * This component owns all application state and controls which step
 * is currently displayed. It does not render any UI of its own —
 * it delegates entirely to the step components.
 *
 * STEP FLOW:
 *   0 — IntakeStep    (name, email, org, size, sector, concern)
 *   1 — DomainStep    (Domain 1: Digital Strategy)
 *   2 — DomainStep    (Domain 2: Data & Governance)
 *   3 — DomainStep    (Domain 3: People & Skills)
 *   4 — DomainStep    (Domain 4: Technology & Architecture)
 *   5 — DomainStep    (Domain 5: Service Management)
 *   6 — DomainStep    (Domain 6: AI & Machine Learning)
 *   7 — ResultsStep   (Radar chart, narratives, PDF download)
 *
 * STATE MANAGED HERE:
 *   currentStep       {number} - Index of the active step (0–7)
 *   intakeAnswers     {Object} - Answers from IntakeStep { I1: ..., I2: ..., ... }
 *   domainAnswers     {Object} - All domain question scores { "D1.Q1": 3, ... }
 *   perceptionAnswers {Object} - All perception gap deltas { "D1.PG": 1, ... }
 *   results           {Object} - Calculated results from scoring.js
 *
 * BACK NAVIGATION:
 *   Each DomainStep receives its existing answers so that navigating
 *   Back and then Forward again does not clear previous responses.
 *
 * Connected files:
 *   - IntakeStep.jsx        step 0
 *   - DomainStep.jsx        steps 1–6
 *   - ResultsStep.jsx       step 7
 *   - scoring.js            calculateResults() called when entering Results
 *   - cawood-assessment.css imported here as the single stylesheet entry point
 *   - questions.js          DOMAINS array drives the domain step sequence
 */

import { useState, useEffect } from "react";
import IntakeStep  from "./steps/IntakeStep.jsx";
import DomainStep  from "./steps/DomainStep.jsx";
import ResultsStep from "./steps/ResultsStep.jsx";
import { DOMAINS } from "./data/questions.js";
import { calculateResults } from "./utils/scoring.js";
import "./cawood-assessment.css";

// ─── Step index constants ─────────────────────────────────────────────────────

const STEP_INTAKE  = 0;
const STEP_RESULTS = DOMAINS.length + 1; // step 7

// ─── Root component ───────────────────────────────────────────────────────────

export default function CawoodAssessment() {

  // ── Application state ──────────────────────────────────────────────────────

  const [currentStep,        setCurrentStep]        = useState(STEP_INTAKE);
  const [intakeAnswers,      setIntakeAnswers]       = useState({});
  const [domainAnswers,      setDomainAnswers]       = useState({});
  const [perceptionAnswers,  setPerceptionAnswers]   = useState({});

  // Cache of per-domain answers keyed by domain index — restores state on Back
  const [domainCache,        setDomainCache]         = useState({});

  // Calculated results — populated when the last domain is completed
  const [results,            setResults]             = useState(null);

  // ── Deep-link: load results from R2 if ?token present on mount ────────────
  const CORE_API = process.env.REACT_APP_CORE_API_URL || "https://api.cawood.io";

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) return;

    fetch(`${CORE_API}/user/submission/${token}/results-json`, {
      credentials: "include",
    })
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(data => {
        if (data) {
          setResults(data);
          setCurrentStep(STEP_RESULTS);
        }
      })
      .catch(() => {}); // fall through to normal intake flow on any error
  }, []);

  // ── Handlers ───────────────────────────────────────────────────────────────

  /**
   * IntakeStep calls this when the intake form is submitted successfully.
   * @param {Object} answers - { I1: "Jane Smith", I2: "jane@org.com", ... }
   */
  function handleIntakeComplete(answers) {
    setIntakeAnswers(answers);
    setCurrentStep(STEP_INTAKE + 1);
    scrollToTop();
  }

  /**
   * DomainStep calls this when the respondent clicks Next on a domain.
   * @param {number} domainIndex - Zero-based domain index (0–5)
   * @param {Object} domainData  - { questions: { "D1.Q1": 3, ... }, perception: 1 }
   */
  function handleDomainComplete(domainIndex, domainData) {
    const domain = DOMAINS[domainIndex];

    // Merge question answers into the flat lookup used by scoring.js
    const newDomainAnswers = { ...domainAnswers, ...domainData.questions };
    setDomainAnswers(newDomainAnswers);

    // Store perception gap answer keyed by the domain's PG question ID
    const newPerceptionAnswers = {
      ...perceptionAnswers,
      [domain.perceptionGap.id]: domainData.perception,
    };
    setPerceptionAnswers(newPerceptionAnswers);

    // Cache full domain data for Back navigation
    setDomainCache((prev) => ({ ...prev, [domainIndex]: domainData }));

    const nextStep = currentStep + 1;

    // Calculate results before rendering ResultsStep
    if (nextStep === STEP_RESULTS) {
      const calculatedResults = calculateResults(
        intakeAnswers,
        newDomainAnswers,
        newPerceptionAnswers
      );
      setResults(calculatedResults);
    }

    setCurrentStep(nextStep);
    scrollToTop();
  }

  /**
   * DomainStep calls this when the respondent clicks Back.
   */
  function handleBack() {
    setCurrentStep((prev) => Math.max(STEP_INTAKE, prev - 1));
    scrollToTop();
  }

  // ── Scroll helper ──────────────────────────────────────────────────────────

  function scrollToTop() {
    const el = document.getElementById("ca-assessment-root");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // ── Derived values ─────────────────────────────────────────────────────────

  // Domain index is step number minus 1 (step 1 = domain 0)
  const domainIndex   = currentStep - 1;
  const currentDomain = DOMAINS[domainIndex] ?? null;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div id="ca-assessment-root" className="ca-assessment">

      {/* Step 0 — Intake form */}
      {currentStep === STEP_INTAKE && (
        <IntakeStep onComplete={handleIntakeComplete} />
      )}

      {/* Steps 1–6 — One domain at a time */}
      {currentDomain && currentStep < STEP_RESULTS && (
        <DomainStep
          key={currentDomain.id}
          domain={currentDomain}
          domainIndex={domainIndex}
          totalDomains={DOMAINS.length}
          existingAnswers={domainCache[domainIndex] ?? null}
          onComplete={(data) => handleDomainComplete(domainIndex, data)}
          onBack={handleBack}
        />
      )}

      {/* Step 7 — Results */}
      {currentStep === STEP_RESULTS && results && (
        <ResultsStep results={results} />
      )}

    </div>
  );
}
